import { Button, Group, Stack, TextInput } from "@mantine/core";
import { schemaResolver, useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { createGarageInputSchema } from "@/services/garages/create-garage";

import type { CreateGarageInput } from "@/services/garages/create-garage";
import type { FC } from "react";

export type CreateGarageFormProps = {
  initialValues?: Partial<CreateGarageInput>;
  labels?: {
    cancelButton?: string;
    submitButton?: string;
  };
  loading?: boolean;
  onCancel?: () => void;
  onSubmit: (data: CreateGarageInput) => void;
};

export const CreateGarageForm: FC<CreateGarageFormProps> = ({
  initialValues,
  labels,
  loading = false,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const form = useForm<CreateGarageInput>({
    mode: "controlled",
    initialValues: {
      name: "",
      ...initialValues,
    },
    validate: schemaResolver(createGarageInputSchema, { sync: true }),
    validateInputOnBlur: true,
  });

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="md" py="xs">
        <TextInput
          key={form.key("name")}
          placeholder={t("components.CreateGarageForm.namePlaceholder")}
          {...form.getInputProps("name")}
        />
        <Group justify="center" mt="lg">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              aria-label={t("components.Form.cancelButton")}
            >
              {labels?.cancelButton || t("components.Form.cancelButton")}
            </Button>
          )}
          <Button
            type="submit"
            disabled={!form.isValid()}
            loading={loading}
            aria-label={t("components.CreateGarageForm.submitButton")}
          >
            {labels?.submitButton || t("components.CreateGarageForm.submitButton")}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
